import { Helmet } from "react-helmet-async";
import Hero from "../components/Hero/Hero";
import AboutMe from "../components/AboutMe/AboutMe";
import Contact from "../components/Contact/Contact";
import CaseStudies from "../components/CaseStudies/CaseStudies";
import ServiceSpectrum from "../components/ServiceSpectrum/ServiceSpectrum";
import Process from "../components/Process/Process";
import Preloader from "../components/Preloader/Preloader";
import Navbar from "../components/Navbar/Navbar";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useRef, useState, useMemo } from "react";
import Companies from "../components/Companies/Companies";
import { useSectionBackgrounds } from "../hooks/useSectionBackgrounds";
import PaletteSelector from "../components/PaletteSelector/PaletteSelector";
import { TIME_OF_DAY_BACKGROUNDS } from "../theme/timeOfDay";
import { useContextAwareness } from "../hooks/useContextAwareness";

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

function HomePage() {
  const el = useRef();
  const q = gsap.utils.selector(el);
  const [preloaderFinished, setPreloaderFinished] = useState(false);
  // Palette follows the visitor's time of day unless they pick one (dock)
  const { timeOfDay: autoTimeOfDay } = useContextAwareness();
  const [pickedTimeOfDay, setPickedTimeOfDay] = useState(null);
  const timeOfDay = pickedTimeOfDay || autoTimeOfDay;

  const backgrounds = TIME_OF_DAY_BACKGROUNDS[timeOfDay] || TIME_OF_DAY_BACKGROUNDS.afternoon;

  const sectionBackgrounds = useMemo(() => [
    { trigger: ".about", color: backgrounds.about },
    { trigger: ".spectrum", color: backgrounds.section },
    { trigger: ".process", color: backgrounds.section },
    { trigger: ".casestudies", color: backgrounds.section },
    { trigger: ".contact", color: backgrounds.section },
  ], [backgrounds]);

  // --- Animations ---


  // Footer Animation - blur stagger
  // Footer Animation - blur stagger with mobile optimization
  useGSAP(() => {
    if (!preloaderFinished) return;

    const mm = gsap.matchMedia();
    // Desktop
    mm.add("(min-width: 900px)", () => {
      gsap.fromTo(q(".footer"),
        { filter: "blur(20px)", opacity: 0 },
        {
          filter: "blur(0px)",
          opacity: 1,
          scrollTrigger: {
            trigger: q(".footer"),
            start: "top 80%",
            end: "top 40%",
            scrub: true
          }
        }
      );
    });
    // Mobile
    mm.add("(max-width: 899px)", () => {
      gsap.fromTo(q(".footer"),
        { filter: "blur(10px)", opacity: 0 }, // Less blur for performance
        {
          filter: "blur(0px)",
          opacity: 1,
          scrollTrigger: {
            trigger: q(".footer"),
            start: "top 90%", // Trigger earlier on mobile
            end: "top 60%",
            scrub: true
          }
        }
      );
    });
  }, { scope: el, dependencies: [preloaderFinished] });

  // Navbar Colors
  useSectionBackgrounds(el, sectionBackgrounds, backgrounds.base, preloaderFinished, pickedTimeOfDay ? backgrounds.base : null);


  return (
    <div ref={el} data-theme={timeOfDay}>
      <Helmet>
        <title>Mohamed Ayoub Chebbi | Design Engineer for Human-Centered AI Products</title>
        <meta
          name="description"
          content="Mohamed Ayoub Chebbi (Ayoub Chebbi) is a design engineer for human-centered AI products: engineering interfaces that think. Ex Forward Deployed Engineer at BMW Group, now at IP Paris. Products, websites and brands, from first sketch to shipped code."
        />
        <link rel="canonical" href="https://chebbimedayoub.com/" />
        <meta property="og:title" content="Mohamed Ayoub Chebbi | Design Engineer for Human-Centered AI Products" />
        <meta
          property="og:description"
          content="Engineering interfaces that think. Design engineer for human-centered AI products. Ex Forward Deployed Engineer at BMW Group."
        />
        <meta property="og:url" content="https://chebbimedayoub.com/" />
        <meta name="twitter:title" content="Mohamed Ayoub Chebbi | Design Engineer for Human-Centered AI Products" />
        <meta
          name="twitter:description"
          content="Engineering interfaces that think. Design engineer for human-centered AI products. Ex Forward Deployed Engineer at BMW Group."
        />
        {/* Person + WebSite JSON-LD live in public/index.html, visible without JS */}
      </Helmet>
      <div className="blur"></div>
      <Navbar intro={preloaderFinished} />
      <PaletteSelector value={timeOfDay} onChange={setPickedTimeOfDay} />
      <main className="App-header">
        <Preloader
          onLoadComplete={() => setPreloaderFinished(true)}
        />
        <Hero startAnimation={preloaderFinished} />
        <Companies />
        <AboutMe />
        <ServiceSpectrum />
        <Process />
        <CaseStudies />
        <div className="footer">
          <Contact />
        </div>

      </main>
    </div>
  );
}

export default HomePage;
